import { useState, type MouseEvent } from 'react';
import { Button, Link, Stack, Typography } from '@mui/material';
import { useLocation, useSearchParams } from 'react-router';
import { RefundQueryParams } from '../resource-description/interfaces';
import { InvoiceWizardStepIds, type InvoiceWizardProps } from './interfaces';
import { Details } from './steps/details';
import { InvoiceForm } from './steps/invoice-form';
import { Summary } from './steps/summary';
import { refundRequestToForm } from './utils/refund-request-to-form';
import { useWizard } from '@/context/wizard';
import { Wizard } from '@/context/wizard/components/wizard';
import { WizardProvider } from '@/context/wizard/context';
import { RefundStatus, StepStatus } from '@/models/subsidy/status';
import { AccordionStep } from '@/packages/subsidy/pages/refund-request/components/step-wrapper';
import { useRefundRequest } from '@/packages/subsidy/pages/refund-request/context';
import { formatDate } from '@/utils/format-date';

const steps = {
  [InvoiceWizardStepIds.InvoiceForm]: { component: InvoiceForm },
  [InvoiceWizardStepIds.Summary]: { component: Summary },
  [InvoiceWizardStepIds.Details]: { component: Details },
};

interface DescriptionProps {
  description: string;
  expanded: boolean;
}

const Description = ({ description, expanded }: DescriptionProps) => {
  const { refundStatus } = useRefundRequest();
  const {
    actions: { navigate },
  } = useWizard<InvoiceWizardProps>();

  const canEdit = refundStatus === RefundStatus.RefundRequestPendingApproval;

  const revisionRequired =
    refundStatus === RefundStatus.RefundRequestRevisionRequired;

  const goToInvoiceForm = () => {
    navigate(InvoiceWizardStepIds.InvoiceForm);
  };

  const handleEditClick = (e: MouseEvent) => {
    if (expanded) e.stopPropagation();
    goToInvoiceForm();
  };

  return (
    <Stack spacing={1} alignItems="start">
      <Typography>{description}</Typography>
      {canEdit && <Link onClick={handleEditClick}>Editar</Link>}
      {revisionRequired && (
        <Button variant="contained" onClick={handleEditClick}>
          Corrigir
        </Button>
      )}
    </Stack>
  );
};

const InvoiceAttachmentContent = () => {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const stepParam = searchParams.get('step');
  const openFromUrl =
    stepParam !== null && stepParam === RefundQueryParams.Invoice;
  const { getStepState, resourceApproval } = useRefundRequest();
  const { status, disabled } = getStepState(3);
  const { currentStepId } = useWizard<InvoiceWizardProps>();

  const defaultExpanded = openFromUrl && !pathname.includes('novo');
  const [expanded, setExpanded] = useState(defaultExpanded);

  const createdAt = resourceApproval?.refund_request?.created_at;
  const formattedDate = formatDate(createdAt || '');
  const description = `Enviado em ${formattedDate}`;

  const hasRefundRequest = !!resourceApproval?.refund_request;
  const showDescription =
    hasRefundRequest && currentStepId === InvoiceWizardStepIds.Details;

  return (
    <AccordionStep
      key={`step3-${pathname}-${stepParam ?? ''}`}
      title="3. Envio de comprovantes"
      stepStatus={hasRefundRequest ? (status ?? StepStatus.Success) : null}
      disabled={disabled}
      defaultExpanded={defaultExpanded}
      isExpanded={expanded}
      onChange={(_, next) => setExpanded(next)}
      description={
        showDescription && (
          <Description description={description} expanded={expanded} />
        )
      }
    >
      <Wizard steps={steps} />
    </AccordionStep>
  );
};

export const InvoiceAttachment = () => {
  const { resourceApproval } = useRefundRequest();

  const currentStepId = resourceApproval?.refund_request
    ? InvoiceWizardStepIds.Details
    : InvoiceWizardStepIds.InvoiceForm;

  const data = resourceApproval?.refund_request
    ? refundRequestToForm(resourceApproval?.refund_request)
    : null;

  const initialState = { currentStepId, values: { data } };

  return (
    <WizardProvider initialState={initialState}>
      <InvoiceAttachmentContent />
    </WizardProvider>
  );
};
